import React from 'react';
import {Link} from 'react-router-dom';
import {AdminWordlist} from '../../domain/adminWordlists';
import {EmptyState, ErrorState, LoadingState, Timestamp, formatCount} from './AdminPrimitives';
import {GlobeIcon, StarIcon, TrashIcon} from '../Icons';

interface AdminWordlistsTableProps {
  wordlists: AdminWordlist[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  hasFilters: boolean;
}

/**
 * Every catalog list, any owner, any state. Unlike the user table there is nothing to sort by
 * besides created-at direction (the endpoint's own `order` param), so there are no clickable
 * column headers here.
 *
 * The owner column links straight to the admin user page — `user_id` on this row is
 * `public.users.id`, the same id `/admin/users/:id` takes, so no second lookup is needed to make it
 * clickable.
 */
const AdminWordlistsTable: React.FC<AdminWordlistsTableProps> = ({
  wordlists,
  isLoading,
  error,
  onRetry,
  hasFilters,
}) => {
  if (error && wordlists.length === 0) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }

  if (isLoading && wordlists.length === 0) {
    return <LoadingState message="Loading word lists…" />;
  }

  if (wordlists.length === 0) {
    return (
      <EmptyState
        message={hasFilters ? 'No word lists match these filters.' : 'No word lists yet.'}
        hint={hasFilters ? 'Clear the filters to see everything.' : undefined}
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[820px] text-left text-xs">
        <thead>
          <tr className="border-b border-gray-800 bg-gray-950/40 text-[11px] uppercase tracking-wide text-gray-500">
            <th scope="col" className="px-4 py-2 font-medium">Name</th>
            <th scope="col" className="px-4 py-2 font-medium text-right">Words</th>
            <th scope="col" className="px-4 py-2 font-medium">Owner</th>
            <th scope="col" className="px-4 py-2 font-medium">Created</th>
            <th scope="col" className="px-4 py-2" />
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-800/60">
          {wordlists.map(list => (
            <tr
              key={list.id}
              className={`transition-colors hover:bg-gray-900/60 ${list.isDeleted ? 'opacity-60' : ''}`}
            >
              <td className="max-w-[320px] px-4 py-2.5">
                <Link to={`/admin/wordlists/${list.id}`} className="block min-w-0">
                  <span className="block truncate text-gray-100 hover:text-indigo-300">{list.name}</span>
                  {list.description && (
                    <span className="block truncate text-[11px] text-gray-500">{list.description}</span>
                  )}
                </Link>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {list.isOfficial && (
                    <span title="Official list" className="shrink-0 text-amber-400">
                      <StarIcon className="h-3.5 w-3.5" />
                    </span>
                  )}
                  {list.isPublic && (
                    <span title="Public — visible in the marketplace" className="shrink-0 text-emerald-400">
                      <GlobeIcon className="h-3.5 w-3.5" />
                    </span>
                  )}
                  {list.isDeleted && (
                    <span title="Soft-deleted" className="shrink-0 text-red-400">
                      <TrashIcon className="h-3.5 w-3.5" />
                    </span>
                  )}
                </div>
              </td>

              <td className="px-4 py-2.5 text-right tabular-nums text-gray-300">
                {formatCount(list.wordCount)}
              </td>

              <td className="px-4 py-2.5">
                <Link
                  to={`/admin/users/${list.userId}`}
                  className="font-mono text-[11px] text-indigo-400 hover:text-indigo-300"
                >
                  {list.userId.slice(0, 8)}…
                </Link>
              </td>

              <td className="px-4 py-2.5 text-gray-400">
                <Timestamp iso={list.createdAt} />
              </td>

              <td className="px-4 py-2.5 text-right">
                <Link
                  to={`/admin/wordlists/${list.id}`}
                  className="text-[11px] text-indigo-400 hover:text-indigo-300"
                >
                  Open
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminWordlistsTable;
